import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AdminApiService, CountryStatistics } from '../../services/adminApiService';
import './AnalyticsDashboard.css';

const COLORS = ['#667eea', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1'];

export const AnalyticsDashboard: React.FC = () => {
  const [userGrowth, setUserGrowth] = useState<any[]>([]);
  const [guestActivity, setGuestActivity] = useState<any[]>([]);
  const [gameActivity, setGameActivity] = useState<any[]>([]);
  const [winRates, setWinRates] = useState<any[]>([]);
  const [popularModes, setPopularModes] = useState<any[]>([]);
  const [peakUsage, setPeakUsage] = useState<any[]>([]);
  const [countryStats, setCountryStats] = useState<CountryStatistics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        userGrowthResult,
        guestActivityResult,
        gameActivityResult,
        winRatesResult,
        popularModesResult,
        peakUsageResult,
        countryStatsResult,
      ] = await Promise.all([
        AdminApiService.getUserGrowthAnalytics(period),
        AdminApiService.getGuestActivityAnalytics(period),
        AdminApiService.getGameActivityAnalytics(period),
        AdminApiService.getWinRatesAnalytics(),
        AdminApiService.getPopularGameModes(10),
        AdminApiService.getPeakUsageTimes(),
        AdminApiService.getCountryStatistics(),
      ]);

      if (userGrowthResult.error) setError(userGrowthResult.error);
      else setUserGrowth(userGrowthResult.analytics);

      if (guestActivityResult.error) console.error('Guest activity error:', guestActivityResult.error);
      else setGuestActivity(guestActivityResult.analytics);

      if (gameActivityResult.error) console.error('Game activity error:', gameActivityResult.error);
      else setGameActivity(gameActivityResult.analytics);

      if (winRatesResult.error) console.error('Win rates error:', winRatesResult.error);
      else setWinRates(winRatesResult.analytics);

      if (popularModesResult.error) console.error('Popular modes error:', popularModesResult.error);
      else setPopularModes(popularModesResult.analytics);

      if (peakUsageResult.error) console.error('Peak usage error:', peakUsageResult.error);
      else setPeakUsage(peakUsageResult.analytics);

      if (countryStatsResult.error) console.error('Country stats error:', countryStatsResult.error);
      else setCountryStats(countryStatsResult.statistics);
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Format peak usage data for 24-hour display
  const formatPeakUsage = () => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0 }));
    peakUsage.forEach((item) => {
      if (item.hour >= 0 && item.hour < 24) {
        hours[item.hour].count = item.count;
      }
    });
    return hours.map((h) => ({ hour: `${h.hour}:00`, count: h.count }));
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="analytics-loading">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>Analytics Dashboard</h2>
        <div className="analytics-controls">
          <label>Period:</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <button onClick={loadAnalytics} className="analytics-refresh-btn">Refresh</button>
        </div>
      </div>

      {error && <div className="analytics-error">{error}</div>}

      <div className="analytics-grid">
        {/* Daily Activity Chart - Members + Guests */}
        <div className="analytics-card">
          <h3>📊 Daily Activity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={(() => {
              // Combine user growth and guest activity by period
              const combinedMap = new Map<string, { period: string; members: number; guests: number; total: number }>();
              
              userGrowth.forEach(item => {
                combinedMap.set(item.period, {
                  period: item.period,
                  members: item.count || 0,
                  guests: 0,
                  total: item.count || 0
                });
              });
              
              guestActivity.forEach(item => {
                const existing = combinedMap.get(item.period);
                if (existing) {
                  existing.guests = item.count || 0;
                  existing.total = existing.members + existing.guests;
                } else {
                  combinedMap.set(item.period, {
                    period: item.period,
                    members: 0,
                    guests: item.count || 0,
                    total: item.count || 0
                  });
                }
              });
              
              return Array.from(combinedMap.values()).sort((a, b) => a.period.localeCompare(b.period));
            })()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="members" stroke="#667eea" name="New Members" />
              <Line type="monotone" dataKey="guests" stroke="#28a745" name="New Guest Sessions" />
              <Line type="monotone" dataKey="total" stroke="#ffc107" name="Total Activity" strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth Chart */}
        <div className="analytics-card">
          <h3>User Growth (Members Only)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#667eea" name="New Users" />
              <Line type="monotone" dataKey="cumulative" stroke="#28a745" name="Total Users" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Game Activity Chart */}
        <div className="analytics-card">
          <h3>Game Activity</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={gameActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#667eea" name="Games Played" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Win Rates Chart */}
        <div className="analytics-card">
          <h3>Win Rates</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={winRates}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.result}: ${entry.percentage}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {winRates.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Popular Game Modes */}
        <div className="analytics-card">
          <h3>Popular Game Modes</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={popularModes} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="game_mode" type="category" width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#667eea" name="Games" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Peak Usage Times */}
        <div className="analytics-card full-width">
          <h3>Peak Usage Times (24-Hour)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formatPeakUsage()}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#667eea" name="Games Played" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Country Statistics - Only show if there are known countries */}
        {countryStats.length > 0 && (
          <>
            <div className="analytics-card full-width">
              <h3>🌍 Member Distribution by Country</h3>
              <div style={{ marginBottom: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', flex: '1', minWidth: '200px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                    {countryStats.reduce((sum, c) => sum + c.memberCount, 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Members with Known Countries</div>
                </div>
                <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', flex: '1', minWidth: '200px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                    {countryStats.length}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Countries Represented</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={countryStats.slice(0, 20)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="countryName" type="category" width={150} />
                  <Tooltip 
                    formatter={(value: any) => [`${value} Members`, 'Members']}
                    labelFormatter={(label) => `Country: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="memberCount" fill="#667eea" name="Members" />
                </BarChart>
              </ResponsiveContainer>
              {countryStats.length > 20 && (
                <div style={{ marginTop: '10px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                  Showing top 20 countries (out of {countryStats.length} total)
                </div>
              )}
            </div>

            {/* Country Table */}
            <div className="analytics-card full-width">
              <h3>📊 Detailed Country Statistics</h3>
              <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Country</th>
                      <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countryStats.map((stat, index) => (
                      <tr key={stat.countryCode} style={{ borderBottom: '1px solid #dee2e6', background: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          <span style={{ marginRight: '8px' }}>🌍</span>
                          {stat.countryName} ({stat.countryCode})
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#667eea', fontWeight: '600' }}>
                          {stat.memberCount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

